import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { reportService } from "../services/report.service";
import { CreateReportDto, ReportFilters } from "../types/dtos/report";
import { useOrganization } from "../../contexts/OrganizationContext";

const REPORTS_KEY = "reports";

export const useReports = (filters?: ReportFilters) => {
  const { selectedOrganization } = useOrganization();
  return useQuery({
    queryKey: [REPORTS_KEY, selectedOrganization?.id, filters],
    queryFn: () => reportService.findAllPaginated(filters).then((res) => res.data),
    enabled: !!selectedOrganization?.id,
    staleTime: 10000,
  });
};

export const useReport = (id: string) => {
  const { selectedOrganization } = useOrganization();
  return useQuery({
    queryKey: [REPORTS_KEY, selectedOrganization?.id, id],
    queryFn: () => reportService.findById(id).then((res) => res.data),
    enabled: !!id && !!selectedOrganization?.id,
  });
};

export const useRequestReport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateReportDto) =>
      reportService.requestReport(data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [REPORTS_KEY] });
    },
  });
};

export const useDownloadReport = () => {
  return useMutation({
    mutationFn: (id: string) => reportService.getDownloadUrl(id).then((res) => res.data),
  });
};

export const useDeleteReport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => reportService.delete(id).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [REPORTS_KEY] });
    },
  });
};

export const useReportTemplateContentByReport = (id: string) => {
  const { selectedOrganization } = useOrganization();
  return useQuery({
    queryKey: [REPORTS_KEY, selectedOrganization?.id, "template-content", id],
    queryFn: () => reportService.getTemplateContent(id).then((res) => res.data),
    enabled: !!id && !!selectedOrganization?.id,
  });
};

export const useReportTargetOrgs = (reportId: string | null) => {
  const { selectedOrganization } = useOrganization();
  return useQuery({
    queryKey: [REPORTS_KEY, selectedOrganization?.id, "target-orgs", reportId],
    queryFn: () => reportService.getTargetOrgs(reportId!).then((res) => res.data),
    enabled: !!reportId && !!selectedOrganization?.id,
  });
};
